import type { IOfflineChannelConfiguration } from '@microsoft/applicationinsights-offlinechannel-js';

/**
 * The offline channel's storage key prefix: the localStorage key and the IndexedDB database name both start with it
 * followed by an underscore, which is how {@link purgeOfflineTelemetry} finds them. Ours rather than the SDK's
 * default, so the purge can never touch storage some other Application Insights instance owns. It must not contain an
 * underscore, since the SDK uses one as its separator.
 */
export const OFFLINE_STORAGE_PREFIX = 'RaptorTelemetry';

/**
 * How long a stored item is kept before it is dropped: two days. Application Insights ingestion is not built for
 * items much older than that, and a device that has been offline for days is better served by current telemetry than
 * by a backlog. Expired items are cleaned when telemetry starts (`autoClean`).
 */
export const OFFLINE_MAX_STORAGE_AGE_MS = 48 * 60 * 60 * 1000;

/**
 * The most the localStorage fallback may hold. localStorage is a few megabytes shared with the app's own session and
 * settings, so telemetry keeps to a fifth of a typical quota; a write that would exceed it is refused. IndexedDB, the
 * preferred store, has no byte limit in the offline channel: it is bounded by {@link OFFLINE_MAX_STORAGE_AGE_MS}, and
 * when the browser's quota is hit the oldest batches are dropped to make room.
 */
export const OFFLINE_MAX_LOCAL_STORAGE_BYTES = 1_000_000;

/**
 * The IndexedDB schema version the offline channel puts in its database name. Only used to name the database when the
 * WebView cannot list its databases.
 */
const OFFLINE_DB_VERSION = 1;

/** The ingestion host the SDK uses when the connection string names none. */
const DEFAULT_INGESTION_HOST = 'dc.services.visualstudio.com';

/** The offline channel's online state values (the core's `eOfflineValue`, a const enum that cannot be imported). */
export const ONLINE_STATE = 1;

/** See {@link ONLINE_STATE}. */
export const OFFLINE_STATE = 2;

/** The part of the SDK's offline listener (`IOfflineListener`) used to tell it whether the device is connected. */
export interface OnlineStateListener {
    setOnlineState(state: number): void;
}

/**
 * The offline channel's configuration: IndexedDB first, falling back to localStorage where IndexedDB is unavailable,
 * with the age and size limits above.
 *
 * @param providers The channel's `StorageProviders` values for IndexedDB and localStorage, from the loaded module,
 *                  since the enum is a const enum that cannot be referenced before it loads.
 * @returns The configuration for `extensionConfig[offlineChannel.identifier]`.
 */
export function createOfflineChannelConfig(providers: { IndexedDb: number; LocalStorage: number }): IOfflineChannelConfiguration {
    return {
        providers: [providers.IndexedDb, providers.LocalStorage],
        storageKeyPrefix: OFFLINE_STORAGE_PREFIX,
        inStorageMaxTime: OFFLINE_MAX_STORAGE_AGE_MS,
        autoClean: true,
        maxStorageSizeInBytes: OFFLINE_MAX_LOCAL_STORAGE_BYTES,
    };
}

/**
 * The name of the IndexedDB database the offline channel uses for this connection string: the prefix, the schema
 * version and the ingestion host (the channel's `getEndpointDomain`).
 *
 * @param connectionString The Application Insights connection string.
 * @returns The database name.
 */
export function offlineDatabaseName(connectionString: string): string {
    const endpoint = connectionString
        .split(';')
        .map((part) => part.trim())
        .find((part) => part.toLowerCase().startsWith('ingestionendpoint='))
        ?.slice('ingestionendpoint='.length);
    let host = DEFAULT_INGESTION_HOST;
    if (endpoint) {
        try {
            host = new URL(endpoint).host.replace(/^www\./, '');
        } catch {
            host = DEFAULT_INGESTION_HOST;
        }
    }
    return `${OFFLINE_STORAGE_PREFIX}_${OFFLINE_DB_VERSION}_${host}`;
}

/**
 * Deletes everything the offline channel has stored, so items kept while offline are never sent after telemetry has
 * been stopped. Works without loading the SDK, so a branch with telemetry off clears what an earlier session left
 * behind without the SDK ever being downloaded. Storage the WebView refuses to touch is left alone rather than failing
 * the stop.
 *
 * The database is deleted rather than emptied: an open connection from an SDK instance that is unloading closes itself
 * on the deletion's version change, so the deletion runs once its last write has finished, and a new instance opening
 * the database waits for the deletion and starts empty.
 *
 * @param connectionString The connection string, which names the database when the WebView cannot list them.
 * @returns Resolves once the deletions have been requested.
 */
export async function purgeOfflineTelemetry(connectionString: string): Promise<void> {
    const keyPrefix = `${OFFLINE_STORAGE_PREFIX}_`;
    for (const storage of [globalThis.localStorage, globalThis.sessionStorage]) {
        try {
            if (!storage) {
                continue;
            }
            const keys: string[] = [];
            for (let index = 0; index < storage.length; index++) {
                const key = storage.key(index);
                if (key?.startsWith(keyPrefix)) {
                    keys.push(key);
                }
            }
            keys.forEach((key) => storage.removeItem(key));
        } catch {
            continue;
        }
    }

    const indexedDb = globalThis.indexedDB;
    if (!indexedDb) {
        return;
    }
    const listed = (await listDatabaseNames(indexedDb)).filter((name) => name.startsWith(keyPrefix));
    for (const name of new Set([offlineDatabaseName(connectionString), ...listed])) {
        try {
            indexedDb.deleteDatabase(name);
        } catch {
            continue;
        }
    }
}

/**
 * The names of the origin's IndexedDB databases, or none where the WebView cannot list them (`databases()` is missing
 * from older WebViews), in which case the purge relies on the name derived from the connection string.
 */
async function listDatabaseNames(indexedDb: IDBFactory): Promise<string[]> {
    try {
        if (typeof indexedDb.databases !== 'function') {
            return [];
        }
        const databases = await indexedDb.databases();
        return databases.map((database) => database.name ?? '');
    } catch {
        return [];
    }
}
